import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { X, UploadCloud, Loader2, Layers, AlertCircle, CheckCircle2 } from "lucide-react";
import { RowsEditor } from "./PmaxImporter";
import { friendlyRange } from "./client-detail/compare/headlineTotals";

const CHANNELS = { meta: "META Paid", social_mirror: "Social Mirror", mobile: "Mobile", ctv: "CTV", pmax: "PMAX", other: "Other" };
const FILLABLE = ["meta", "social_mirror", "mobile"];
const selectCls = "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm";
const fmt = (n) => Number(n || 0).toLocaleString();

const outcomeText = (ch, own) => {
  if (own && ch === "pmax") return "Has its own report: adds clicks, click rate and the daily chart to it";
  if (own) return "Has its own report: adds detail only, totals unchanged";
  if (FILLABLE.includes(ch)) return "No report of its own yet: uses these numbers until one is uploaded";
  return "Kept with the summary only";
};

const DateChoice = ({ data, client, dates, setDates }) => {
  const rs = data.date_start, re = data.date_end;
  const cs = client?.report_start, ce = client?.report_end;
  const differs = rs && re && cs && ce && (rs !== cs || re !== ce);
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-slate-700 block mb-1">Report start</label>
          <input type="date" value={dates.start} onChange={(e) => setDates({ ...dates, start: e.target.value })} className={selectCls} data-testid="cs-date-start" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-700 block mb-1">Report end</label>
          <input type="date" value={dates.end} onChange={(e) => setDates({ ...dates, end: e.target.value })} className={selectCls} data-testid="cs-date-end" />
        </div>
      </div>
      {differs && (
        <div className="p-3 rounded-lg bg-amber-50 border border-amber-300 text-amber-900 text-xs flex flex-wrap items-center gap-2" data-testid="cs-date-mismatch">
          <span>The report says <b>{friendlyRange(rs, re)}</b>, but this client's report period is <b>{friendlyRange(cs, ce)}</b>.</span>
          <button onClick={() => setDates({ start: rs, end: re })} className="px-2 py-1 rounded bg-amber-600 text-white font-semibold" data-testid="cs-use-report-dates">Use report dates</button>
          <button onClick={() => setDates({ start: cs, end: ce })} className="px-2 py-1 rounded border border-amber-500" data-testid="cs-use-period-dates">Use report period</button>
        </div>
      )}
    </div>
  );
};

const LineItems = ({ items, setItems, status }) => (
  <div className="rounded-lg border border-slate-200 overflow-hidden" data-testid="cs-line-items">
    <div className="px-3 py-2 bg-slate-50 text-xs font-semibold text-slate-700">Line items: check which channel each one belongs to</div>
    {items.map((li, i) => (
      <div key={i} className="px-3 py-2 border-t border-slate-100 flex flex-col md:flex-row md:items-center gap-2" data-testid={`cs-line-item-${i}`}>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-slate-900 truncate" title={li.name}>{li.name}</p>
          <p className="text-[11px] text-slate-500">{fmt(li.impressions)} impressions · {fmt(li.clicks)} clicks</p>
        </div>
        <select value={li.channel} onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, channel: e.target.value } : x)))} className="px-2 py-1.5 border border-slate-300 rounded-lg text-sm md:w-44" data-testid={`cs-line-item-channel-${i}`}>
          {Object.entries(CHANNELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
    ))}
    <div className="px-3 py-2 border-t border-slate-200 bg-slate-50 space-y-1" data-testid="cs-outcomes">
      {[...new Set(items.map((i) => i.channel))].map((ch) => (
        <p key={ch} className="text-[11px] text-slate-700"><b>{CHANNELS[ch]}:</b> {outcomeText(ch, status?.[ch])}</p>
      ))}
      {new Set(items.map((i) => i.channel)).size > 1 && (
        <p className="text-[11px] text-slate-500 italic" data-testid="cs-mixed-note">This report covers more than one channel, so its day-by-day chart, ads and devices stay with the summary instead of going to one channel.</p>
      )}
    </div>
  </div>
);

export default function CampaignSummaryImporter({ open, onClose, clients, API, onImported }) {
  const [step, setStep] = useState("upload");
  const [clientId, setClientId] = useState("");
  const [location, setLocation] = useState("");
  const [file, setFile] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const [daily, setDaily] = useState([]);
  const [dates, setDates] = useState({ start: "", end: "" });
  const [status, setStatus] = useState(null);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);
  const client = clients.find((c) => c.id === clientId);

  useEffect(() => {
    setStatus(null);
    if (!clientId || !dates.start || !dates.end) return;
    const loc = location ? `&location_name=${encodeURIComponent(location)}` : "";
    axios.get(`${API}/campaign-summary/status?client_id=${clientId}&report_start=${dates.start}&report_end=${dates.end}${loc}`)
      .then((r) => setStatus(r.data)).catch(() => setStatus(null));
  }, [API, clientId, dates.start, dates.end, location]);

  const close = () => { setStep("upload"); setClientId(""); setLocation(""); setFile(null); setErrorMsg(""); setData(null); setItems([]); setDaily([]); setResult(null); setDates({ start: "", end: "" }); setStatus(null); onClose(); };

  const runExtract = async () => {
    setErrorMsg(""); setStep("extracting");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/import/campaign-summary/extract?client_id=${clientId}`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 240000 });
      setData(res.data);
      setItems(res.data.line_items || []);
      setDaily(res.data.daily || []);
      setDates({ start: res.data.date_start || client?.report_start || "", end: res.data.date_end || client?.report_end || "" });
      setStep("review");
    } catch (e) {
      setErrorMsg(e.response?.data?.detail || e.message || "Extraction failed"); setStep("upload");
    }
  };

  const runSave = async () => {
    setErrorMsg(""); setStep("saving");
    try {
      const res = await axios.post(`${API}/import/campaign-summary/save`, {
        client_id: clientId, report_start: dates.start, report_end: dates.end, location_name: location, filename: data.filename || file.name,
        impressions: data.impressions || 0, clicks: data.clicks || 0, ctr: data.ctr || 0, national_avg_multiplier: data.national_avg_multiplier || 0,
        line_items: items, creatives: data.creatives || [], device_breakdown: data.device_breakdown || {}, daily,
      }, { timeout: 60000 });
      setResult(res.data); setStep("done");
      onImported?.();
    } catch (e) {
      setErrorMsg(e.response?.data?.detail || e.message || "Save failed"); setStep("review");
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="cs-importer-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-sky-500 flex items-center justify-center text-white"><Layers className="w-5 h-5" /></div>
            <div>
              <p className="text-lg font-bold text-slate-900">Campaign Summary</p>
              <p className="text-xs text-slate-500">Any Campaign Summary Dashboard, one or many line items. Never counts numbers twice.</p>
            </div>
          </div>
          <button onClick={close} className="p-2 rounded-lg hover:bg-slate-100" data-testid="cs-close"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="p-5">
          {errorMsg && (
            <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm" data-testid="cs-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /><span>{errorMsg}</span>
            </div>
          )}
          {step === "upload" && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1">Client</label>
                  <select value={clientId} onChange={(e) => { setClientId(e.target.value); setLocation(""); }} className={selectCls} data-testid="cs-client-select">
                    <option value="">Select client…</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                {client?.client_type === "multi" && (
                  <div>
                    <label className="text-xs font-semibold text-slate-700 block mb-1">Location</label>
                    <select value={location} onChange={(e) => setLocation(e.target.value)} className={selectCls} data-testid="cs-location-select">
                      <option value="">All locations</option>
                      {(client.locations || []).map((l) => <option key={l} value={l}>{l}</option>)}
                    </select>
                  </div>
                )}
              </div>
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); setFile(e.dataTransfer.files?.[0] || null); }}
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-emerald-500 hover:bg-emerald-50/40 transition-colors cursor-pointer"
                data-testid="cs-drop-zone"
              >
                <UploadCloud className="w-10 h-10 mx-auto text-slate-400 mb-2" />
                <p className="text-sm font-medium text-slate-700">{file ? `${file.name} ready` : "Drop the Campaign Summary Dashboard PDF here"}</p>
                <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="cs-file-input" />
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={close} className="px-4 py-2 text-sm rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50" data-testid="cs-cancel">Cancel</button>
                <button onClick={runExtract} disabled={!clientId || !file} className="px-5 py-2 text-sm rounded-lg bg-gradient-to-br from-emerald-600 to-sky-500 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90" data-testid="cs-extract-btn">
                  Read the report
                </button>
              </div>
            </div>
          )}
          {(step === "extracting" || step === "saving") && (
            <div className="py-16 text-center" data-testid={`cs-${step}`}>
              <Loader2 className="w-10 h-10 mx-auto text-emerald-600 animate-spin mb-3" />
              <p className="text-sm font-medium text-slate-700">{step === "extracting" ? "Reading line items and the day-by-day chart… (about a minute)" : "Saving…"}</p>
            </div>
          )}
          {step === "review" && data && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-900 text-xs" data-testid="cs-totals">
                Report totals: <b>{fmt(data.impressions)}</b> impressions · <b>{fmt(data.clicks)}</b> clicks · <b>{data.ctr}%</b> click rate
              </div>
              <DateChoice data={data} client={client} dates={dates} setDates={setDates} />
              <LineItems items={items} setItems={setItems} status={status} />
              <RowsEditor title="Day by Day" cols={[{ key: "date", label: "Date", type: "text" }, { key: "impressions", label: "Impr.", type: "number" }, { key: "clicks", label: "Clicks", type: "number" }]} rows={daily} onChange={setDaily} testIdPrefix="cs-daily" />
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setStep("upload")} className="px-4 py-2 text-sm rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50" data-testid="cs-back">Back</button>
                <button onClick={runSave} disabled={!items.length || !dates.start || !dates.end} className="px-5 py-2 text-sm rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2" data-testid="cs-save-btn">
                  <CheckCircle2 className="w-4 h-4" /> Looks Good, Save
                </button>
              </div>
            </div>
          )}
          {step === "done" && (
            <div className="py-10 text-center" data-testid="cs-done">
              <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
              <p className="text-base font-bold text-slate-900 mb-3">Saved for {friendlyRange(dates.start, dates.end)}</p>
              <div className="inline-block text-left space-y-1 mb-5">
                {Object.entries(result?.channels || {}).map(([ch, c]) => (
                  <p key={ch} className="text-sm text-slate-700" data-testid={`cs-done-${ch}`}>
                    <b>{CHANNELS[ch]}:</b> {c.status === "filled" ? `${fmt(c.impressions)} impressions added` : c.status === "attached" ? (ch === "pmax" ? "clicks and daily chart added to its report" : "daily chart added to its report") : FILLABLE.includes(ch) ? "detail added, totals unchanged" : "kept with the summary only"}
                  </p>
                ))}
              </div>
              <div><button onClick={close} className="px-5 py-2 text-sm rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700" data-testid="cs-done-close">Done</button></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
